import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack,
  MenuItem, Alert,
} from '@mui/material';
import type { Client, CreateClientInput } from '../../api/clients.api';

interface Props {
  open: boolean;
  initial?: Client | null;
  onClose: () => void;
  onSubmit: (input: CreateClientInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateClientInput = { clientName: '', status: 'active' };

export function ClientFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateClientInput>(empty);

  useEffect(() => {
    setForm(
      initial
        ? {
            clientName: initial.clientName,
            gstNumber: initial.gstNumber ?? undefined,
            panNumber: initial.panNumber ?? undefined,
            address: initial.address ?? undefined,
            contactPerson: initial.contactPerson ?? undefined,
            email: initial.email ?? undefined,
            mobile: initial.mobile ?? undefined,
            status: initial.status,
          }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateClientInput>(k: K, v: CreateClientInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Client — ${initial?.clientCode}` : 'Create Client'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Client Name" value={form.clientName} onChange={(e) => set('clientName', e.target.value)} required fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="GST Number" value={form.gstNumber ?? ''} onChange={(e) => set('gstNumber', e.target.value.toUpperCase())} fullWidth />
            <TextField label="PAN" value={form.panNumber ?? ''} onChange={(e) => set('panNumber', e.target.value.toUpperCase())} fullWidth />
          </Stack>
          <TextField label="Address" value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} fullWidth multiline rows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label="Contact Person" value={form.contactPerson ?? ''} onChange={(e) => set('contactPerson', e.target.value)} fullWidth />
            <TextField label="Mobile" value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} fullWidth />
          </Stack>
          <TextField label="Email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} fullWidth />
          <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as CreateClientInput['status'])} fullWidth>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
